import { useQuery } from "@tanstack/react-query";
import { logger } from "@/utils/logger";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/contexts/LocaleContext";

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  action: string | null;
  action_config: Record<string, unknown> | null;
  keywords: string[] | null;
  helpful_count: number;
  not_helpful_count: number;
  view_count: number;
  priority: number;
  source_locale: string;
  // Localized fields (populated when locale differs from source)
  localizedQuestion?: string | null;
  localizedAnswer?: string | null;
  isLocalized?: boolean;
}

export interface UseFAQsOptions {
  limit?: number;
  locationId?: number | null; // Filter by location (null = global only)
}

export const useFAQs = ({ limit = 10, locationId = null }: UseFAQsOptions = {}) => {
  const { settings } = useLocale();
  const targetLocale = settings.locale;

  return useQuery({
    queryKey: ['help-center-faqs', limit, locationId, targetLocale],
    queryFn: async () => {
      // Build the base query
      let query = supabase
        .from('help_center_faqs')
        .select(`
          id,
          question,
          answer,
          category,
          action,
          action_config,
          keywords,
          helpful_count,
          not_helpful_count,
          view_count,
          priority,
          source_locale
        `)
        .eq('is_active', true)
        .order('priority', { ascending: false })
        .limit(limit);

      const { data: faqs, error } = await query;
      if (error) throw error;
      if (!faqs || faqs.length === 0) return [];

      // Get FAQ IDs
      const faqIds = faqs.map(f => f.id);

      // If locationId is provided, filter FAQs by location
      let filteredFaqs = faqs;
      if (locationId !== null) {
        // Fetch help_center_faqs_locations to determine which FAQs apply
        const { data: faqLocations } = await supabase
          .from('help_center_faqs_locations')
          .select('faq_id, location_id')
          .in('faq_id', faqIds);

        // Build a map of faq_id -> location_ids
        const faqLocationMap = new Map<string, number[]>();
        faqLocations?.forEach(fl => {
          if (!faqLocationMap.has(fl.faq_id)) {
            faqLocationMap.set(fl.faq_id, []);
          }
          faqLocationMap.get(fl.faq_id)!.push(fl.location_id);
        });

        // Filter: include FAQ if it's global (no locations) OR matches the locationId
        filteredFaqs = faqs.filter(faq => {
          const locations = faqLocationMap.get(faq.id);
          // No entries = global FAQ
          if (!locations || locations.length === 0) return true;
          // Has entries = check if location matches
          return locations.includes(locationId);
        });
      }

      if (filteredFaqs.length === 0) return [];

      // Check if we need localization
      const needsLocalization = filteredFaqs.some(
        faq => faq.source_locale !== targetLocale
      );

      // Fetch cached localizations from localization_metadata
      let localizationMap = new Map<string, { question?: string; answer?: string }>();
      
      if (needsLocalization) {
        const faqIdsNeedingLocalization = filteredFaqs
          .filter(faq => faq.source_locale !== targetLocale)
          .map(faq => faq.id);

        if (faqIdsNeedingLocalization.length > 0) {
          const { data: localizations } = await supabase
            .from('localization_metadata')
            .select('source_id, translated_name, translated_body')
            .eq('source_type', 'faq')
            .eq('locale', targetLocale)
            .in('source_id', faqIdsNeedingLocalization);

          localizations?.forEach(l => {
            localizationMap.set(l.source_id, {
              question: l.translated_name || undefined,
              answer: l.translated_body || undefined,
            });
          });
        }
      }

      // Map FAQs with localization data
      return filteredFaqs.map(faq => {
        const localization = localizationMap.get(faq.id);
        const isLocalized = faq.source_locale !== targetLocale && !!localization;

        return {
          ...faq,
          helpful_count: faq.helpful_count || 0,
          not_helpful_count: faq.not_helpful_count || 0,
          view_count: faq.view_count || 0,
          priority: faq.priority || 0,
          action_config: faq.action_config as Record<string, unknown> | null,
          localizedQuestion: localization?.question || null,
          localizedAnswer: localization?.answer || null,
          isLocalized,
        } as FAQ;
      });
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const incrementFAQFeedback = async (faqId: string, isHelpful: boolean) => {
  const { error } = await supabase.rpc('increment_faq_feedback', {
    p_faq_id: faqId,
    p_is_helpful: isHelpful,
  });
  if (error) {
    logger.error('Failed to increment FAQ feedback:', error);
    throw error;
  }
};

export const incrementFAQView = async (faqId: string) => {
  try {
    const { error } = await supabase.rpc('increment_faq_view_count', { p_faq_id: faqId });
    if (error) throw error;
  } catch (error) {
    // Silently fail - view count is not critical
    logger.warn('Failed to increment view count:', error);
  }
};
